import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { useViewingOrg } from "@/contexts/viewing-org-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CircleHelp } from "lucide-react";

interface NotificationPresetOption {
  slug: string;
  label: string;
  category: string;
  summary: string;
  bullets: string[];
}

interface ProvisionResponse {
  id: number;
  name: string;
  slug: string;
  application_link_url?: string | null;
}

export function TsmCreateDealerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setViewingOrgId } = useViewingOrg();

  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminFirst, setAdminFirst] = useState("");
  const [adminLast, setAdminLast] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState("");
  const [notificationPresetSlug, setNotificationPresetSlug] = useState("full");
  const [notificationPresetInfoSlug, setNotificationPresetInfoSlug] = useState<string | null>(null);

  const { data: notificationPresetCatalog = [], isLoading: presetsLoading } = useQuery({
    queryKey: ["staff-notification-presets"],
    queryFn: async () => {
      const { data } = await customInstance.get<NotificationPresetOption[]>(
        "/api/organizations/staff-notification-presets/"
      );
      return Array.isArray(data) ? data : [];
    },
  });

  useEffect(() => {
    if (!notificationPresetCatalog.length) return;
    const slugs = new Set(notificationPresetCatalog.map((p) => p.slug));
    if (!slugs.has(notificationPresetSlug)) {
      setNotificationPresetSlug("full");
    }
  }, [notificationPresetCatalog, notificationPresetSlug]);

  const provisionMutation = useMutation({
    mutationFn: async () => {
      const { data } = await customInstance.post<ProvisionResponse>(
        "/api/organizations/tsm-provision-dealer/",
        {
          name: name.trim(),
          contact_email: contactEmail.trim().toLowerCase(),
          contact_phone: contactPhone.trim(),
          admin_email: adminEmail.trim().toLowerCase(),
          admin_first_name: adminFirst.trim(),
          admin_last_name: adminLast.trim(),
          admin_password: adminPassword,
          notification_preset: notificationPresetSlug,
        }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Dealer "${data.name}" created.`);
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setViewingOrgId(data.id);
      navigate("/apps");
    },
    onError: (err: unknown) => {
      const ax = err as {
        response?: { data?: Record<string, string[] | string> };
      };
      const d = ax.response?.data;
      if (d && typeof d === "object") {
        const parts: string[] = [];
        for (const [k, v] of Object.entries(d)) {
          if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
          else if (typeof v === "string") parts.push(v);
        }
        toast.error(parts.join(" ") || "Could not create dealer.");
      } else {
        toast.error("Could not create dealer.");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (adminPassword !== adminPasswordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    provisionMutation.mutate();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create dealer</h1>
          <p className="text-sm text-gray-600 mt-1">
            Creates a dealer under your territory with an organization admin. Points, branding, and
            program settings are copied from the manufacturer organization.
          </p>
        </div>
        <Link to="/apps">
          <Button type="button" variant="outline" size="sm">
            Back to applications
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization &amp; admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="organization"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Organization contact email</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Organization contact phone (10 digits)</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                required
                autoComplete="tel"
                placeholder="e.g. 5551234567"
              />
            </div>

            <div className="space-y-3 border-t pt-4">
              <div>
                <Label className="text-sm font-medium">Organization admin — notification preset</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Sets the first organization admin&apos;s email / in-app notification defaults. They can
                  change this later under their account.
                </p>
              </div>
              {presetsLoading ? (
                <p className="text-sm text-gray-500">Loading notification options…</p>
              ) : notificationPresetCatalog.length === 0 ? (
                <p className="text-sm text-amber-700">
                  Could not load presets. Try refreshing; default &quot;full&quot; will be used if you continue.
                </p>
              ) : (
                <ul className="space-y-3">
                  {notificationPresetCatalog.map((p) => (
                    <li
                      key={p.slug}
                      className="rounded-md border border-gray-200 bg-gray-50/50 px-3 py-2.5 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          name="notification-preset"
                          id={`preset-${p.slug}`}
                          checked={notificationPresetSlug === p.slug}
                          onChange={() => setNotificationPresetSlug(p.slug)}
                          className="mt-1 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`preset-${p.slug}`} className="cursor-pointer block">
                            <span className="text-sm font-medium text-gray-900">{p.label}</span>
                            <span className="block text-xs text-gray-500 mt-0.5">{p.category}</span>
                            <span className="block text-xs text-gray-600 mt-1">{p.summary}</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 p-1 rounded hover:bg-gray-200 text-gray-600"
                          title="What’s included"
                          aria-expanded={notificationPresetInfoSlug === p.slug}
                          aria-label={`Details: ${p.label}`}
                          onClick={() =>
                            setNotificationPresetInfoSlug((cur) => (cur === p.slug ? null : p.slug))
                          }
                        >
                          <CircleHelp className="w-4 h-4" aria-hidden />
                        </button>
                      </div>
                      {notificationPresetInfoSlug === p.slug && (
                        <ul className="text-xs text-gray-600 ml-6 list-disc space-y-1 pb-1">
                          {p.bullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t pt-4 space-y-4">
              <p className="text-sm font-medium text-gray-900">Organization admin (first user)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-first">First name</Label>
                  <Input
                    id="admin-first"
                    value={adminFirst}
                    onChange={(e) => setAdminFirst(e.target.value)}
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-last">Last name</Label>
                  <Input
                    id="admin-last"
                    value={adminLast}
                    onChange={(e) => setAdminLast(e.target.value)}
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">Admin email (login)</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Initial password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password-2">Confirm password</Label>
                <Input
                  id="admin-password-2"
                  type="password"
                  value={adminPasswordConfirm}
                  onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={provisionMutation.isPending}>
                {provisionMutation.isPending ? "Creating…" : "Create dealer"}
              </Button>
              <Link to="/apps">
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
